import { getInsuranceDocMap, zClientInsuranceDocUploads } from '@/general/src/insuranceDoc/shared.js'
import { zDateString, zDateStringOrEmpty } from '@/general/src/other/date.js'
import { z } from 'zod'

const driverLicenseCsvSrc = `Название поля в программе,alias,Название поля на фотографии в документе,Подсказка для ИИ,Валидация,Пример заполнения
Фамилия,lastName,1,,обязательно,МОШНЕНКО
Имя,firstName,2,Первое слово после цифры 2,обязательно,ПЕТР
Отчество,middleName,2,"После имени, может не быть",опционально,ВЛАДИМИРОВИЧ
Дата рождения,birthDate,3,Дата рождения водителя,"обязательно, дата",22.03.1996
Место рождения,birthPlace,3,{{region}} или {{city}},опционально,Самарская обл.
Дата выдачи прав,issuedDate,4a,Дата выдачи прав,"обязательно, дата",29.05.2021
Дата окончания прав,issuedUntil,4b,"Дата окончания действия прав, ровно через 10 лет, после выдачи","обязательно, дата",29.05.2031
Кем выданы права,issuedBy,4с,"Наименование органа, который выдал права, например ""ГИБДД 6318"", где 6318 - это номер отделения",обязательно,ГИБДД 6318
Место регистрации,registrationPlace,8,{{region}} или {{city}},опционально,Самарская обл.
Серия,series,5,"Серия прав, всегда 4 символа, цифры и латинские буквы","обязательно, DD DD",99 20 
Номер,number,5,"Номер прав, оставшиеся цифры после серии, 6 цифр","обязательно, DDDDDD",764321
Категории прав (через запятую),category,9,"Буква или буква с цифрой, бывают: A, B, C, D, E, A1, B1, C1, D1, BE, CE, DE, C1E, D1E, M, Tm, Tb.",обязательно,"В, B1, M"
Дата начала стажа вождения,drivingSince,10,"Это самая первая дата, из тех, что находятся в столбце под номером 10","обязательно, дата",18.06.2011
Пол,gender,,"Это никак не указано в документе напрямую, нужно догадаться исходя из имени, является ли пол ""male"" или ""female""",,`

export const driverLicenseCategories = [
  'A',
  'B',
  'C',
  'D',
  'E',
  'A1',
  'B1',
  'C1',
  'D1',
  'BE',
  'CE',
  'DE',
  'C1E',
  'D1E',
  'M',
  'Tm',
  'Tb',
] as const
export const zDriverLicenseCategoriesString = z
  .string()
  .min(1)
  .refine(
    (val) => {
      const categories = val.split(',').map((cat) => cat.trim())
      return categories.every((cat) =>
        driverLicenseCategories.includes(cat as (typeof driverLicenseCategories)[number])
      )
    },
    { message: `Только такие категории бывают: ${driverLicenseCategories.join(', ')}` }
  )
export const zDriverLicenseCategory = z.enum(driverLicenseCategories)
export const zDriverLicense = z.object({
  lastName: z.string().min(1),
  firstName: z.string().min(1),
  middleName: z.string().min(1),
  birthDate: zDateString,
  // birthPlace: z.string().min(1),
  birthPlace: z.string().optional(),
  issuedDate: zDateString,
  // issuedUntil: zDateString,
  issuedUntil: zDateStringOrEmpty,
  // issuedBy: z.string().min(1),
  issuedBy: z.string().optional(),
  registrationPlace: z.string().min(1),
  series: z.string().min(1),
  number: z.string().min(1),
  category: zDriverLicenseCategoriesString,
  drivingSince: zDateString,
  gender: z.enum(['male', 'female']),
})
export type DriverLicenseWithoutUploads = z.output<typeof zDriverLicense>
export const zDriverLicenseWithUploads = z.object({
  insuranceDocsUploads: zClientInsuranceDocUploads,
  ...zDriverLicense.shape,
})

export const driverLicenseFieldsMap = getInsuranceDocMap({
  csv: driverLicenseCsvSrc,
  extra: {
    insuranceDocsUploads: { label: 'Фотографии водительского удостоверения (лицевая, обратная)' },
  },
  zSchema: zDriverLicenseWithUploads,
})
export const getDriverLicenseFieldTitle = (field: keyof typeof driverLicenseFieldsMap) =>
  driverLicenseFieldsMap[field].label
export const getDriverLicenseFieldHint = (field: keyof typeof driverLicenseFieldsMap) =>
  driverLicenseFieldsMap[field].hint

export const driverLicenseInitialValues: z.infer<typeof zDriverLicenseWithUploads> = {
  insuranceDocsUploads: [],
  lastName: '',
  firstName: '',
  middleName: '',
  birthDate: '',
  birthPlace: '',
  issuedDate: '',
  issuedUntil: '',
  issuedBy: '',
  registrationPlace: '',
  series: '',
  number: '',
  category: '',
  drivingSince: '',
  gender: 'male',
}

// export const driverLicenseExampleValues: z.infer<typeof zDriverLicenseWithUploads> = {
//   insuranceDocsUploads: [],
//   lastName: 'Иванов',
//   firstName: 'Иван',
//   middleName: 'Иванович',
//   birthDate: '01.01.1990',
//   birthPlace: 'Москва',
//   issuedBy: 'Московский ГУВД',
//   registrationPlace: 'Москва',
//   issuedDate: '01.01.2020',
//   issuedUntil: '01.01.2030',
//   series: '1234',
//   number: '123456',
//   category: 'B, C, D',
//   drivingSince: '01.01.2010',
//   gender: 'male',
// }

// export const driverLicenseExampleValues: z.infer<typeof zDriverLicenseWithUploads> = {
//   insuranceDocsUploads: [],
//   lastName: 'Мошненко',
//   firstName: 'Петр',
//   middleName: 'Владимирович',
//   birthDate: '21.05.1993',
//   birthPlace: 'г. Самара',
//   issuedDate: '29.05.2021',
//   issuedUntil: '29.05.2031',
//   issuedBy: 'ГИБДД 6318',
//   registrationPlace: 'Самарская обл',
//   series: '9920',
//   number: '764321',
//   category: 'B,B1,M',
//   drivingSince: '18.06.2011',
//   gender: 'male',
// }

export const driverLicenseExampleValues: z.infer<typeof zDriverLicenseWithUploads> = {
  insuranceDocsUploads: [],
  lastName: 'ЛУЗГИН',
  firstName: 'ИЛЬЯ',
  middleName: 'АНДРЕЕВИЧ',
  birthDate: '15.10.1994',
  birthPlace: 'УЛЬЯНОВСКАЯ ОБЛ.',
  issuedDate: '27.03.2015',
  issuedUntil: '27.03.2025',
  issuedBy: 'ГИБДД 5020',
  registrationPlace: 'УЛЬЯНОВСКАЯ ОБЛ.',
  series: '5021',
  number: '278808',
  category: 'B,B1,M',
  drivingSince: '27.03.2015',
  gender: 'male',
}
